import { Component, Input, Output, OnChanges, SimpleChanges, EventEmitter } from '@angular/core';

type PropertyDisplayConfig = {
  reorderProperties?: string[];
  renameProperties?: { [key: string]: string };
  hideProperties?: string[];
  childConfigs?: { [key: string]: PropertyDisplayConfig };
};

type ChildProperty = {
  key: string;
  value: any;
  isPrimitive: boolean;
};

@Component({
  selector: 'order-object-viewer',
  templateUrl: './order-object-viewer.component.html',
  styleUrl: './order-object-viewer.component.scss',
  standalone: false
})
export class OrderObjectViewerComponent implements OnChanges {

  @Input() public object: any;
  @Input() public customName: string = '';
  @Input() public isArrayItem: boolean = false;
  @Input() public filterText: string = '';
  @Input() public showFriendly: boolean = false;
  @Input() public autoExpand: boolean = false;
  @Input() public hideTopLevelName: boolean = false;
  @Input() public propertyDisplayConfig?: PropertyDisplayConfig;
  
  public name: string = '';
  public isExpanded: boolean = false;
  public isArray: boolean = false;
  public subObjects: { key: string, value: any }[] = [];
  public primitives: { key: string, value: any }[] = [];

  public anyShown: boolean = true;
  public shownSubObjects: { key: string, value: any }[] = [];
  public shownPrimitives: { key: string, value: any }[] = [];
  public shownChildren: ChildProperty[] = [];
  private resolvedChildPropertyDisplayConfigs: { [key: string]: PropertyDisplayConfig } = {};

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['filterText'] && !changes['filterText'].firstChange) ||
      (changes['propertyDisplayConfig'] && !changes['propertyDisplayConfig'].firstChange)) {
      if (changes['propertyDisplayConfig']) {
        this.resolvedChildPropertyDisplayConfigs = {};
      }
      this.setShownObjects();
    }
    if (this.showFriendly) {
      this.object = this.replaceEnumsForFriendlyDisplay(this.object);
      this.object = this.replaceMeausurementUnitsForFriendlyDisplay(this.object);
      this.object = this.removeJunkForFriendlyDisplay(this.object);
      this.object = this.formatDatesForFriendlyDisplay(this.object);
      this.object = this.formatPricingForFriendlyDisplay(this.object);
      this.object = this.removePricingForFriendlyDisplay(this.object);
    }
    this.isExpanded = this.autoExpand;
  }

  ngOnInit() {
    if (this.object) {
      this.isArray = this.object instanceof Array;
      this.name =
        this.customName ||
        this.object?.Information?.ProjectName ||
        (this.object?.FirstName && this.object?.LastName ? `${this.object.FirstName} ${this.object.LastName}` : undefined) ||
        this.object?.Type ||
        this.object?.ItemNumberName ||
        this.object?.Name ||
        this.object?.Description ||
        this.object?.Option ||
        (this.object?.EvapTemperature && this.object?.CondTemperature ? `Evap ${this.object.EvapTemperature} / Cond ${this.object.CondTemperature}` : undefined) ||
        this.object?.Id ||
        this.object?.id ||
        '( Object )';
      for (const key of Object.keys(this.object)) {
        if (this.isHiddenProperty(key)) {
          continue;
        }

        const value = this.object[key];
        if (value === null || value === undefined) {
          continue;
        }
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          this.primitives.push({ key, value });
          if (this.filterText && !key.toLowerCase().includes(this.filterText.toLowerCase()) &&
            !value?.toString()?.toLowerCase()?.includes(this.filterText.toLowerCase())) {
            continue;
          }
        }
        else {
          this.subObjects.push({ key, value });
          if (this.filterText && !this.objectHasValueInTrees(value, this.filterText.toLowerCase())) {
            continue;
          }
        }
      }
      this.setShownObjects();
    }
  }

  setShownObjects() {
    this.shownSubObjects = [];
    this.shownPrimitives = [];
    this.shownChildren = [];
    for (const primitive of this.primitives) {
      if (this.filterText &&
        !primitive.key.toLowerCase().includes(this.filterText.toLowerCase()) &&
        !primitive.value?.toString()?.toLowerCase()?.includes(this.filterText.toLowerCase())) {
        continue;
      }
      this.shownPrimitives.push(primitive);
    }
    for (const subObject of this.subObjects) {
      if (this.filterText && !this.objectHasValueInTrees(subObject.value, this.filterText.toLowerCase())) {
        continue;
      }
      this.shownSubObjects.push(subObject);
    }

    this.shownSubObjects = this.applyConfiguredPropertyOrder(this.shownSubObjects);
    this.shownPrimitives = this.applyConfiguredPropertyOrder(this.shownPrimitives);

    for (const subObject of this.shownSubObjects) {
      this.shownChildren.push({ key: subObject.key, value: subObject.value, isPrimitive: false });
    }

    for (const primitive of this.shownPrimitives) {
      this.shownChildren.push({ key: primitive.key, value: primitive.value, isPrimitive: true });
    }

    this.anyShown = this.shownChildren.length > 0;
  }

  objectHasValueInTrees(obj: any, key: string): boolean {
    for (const subObject of Object.keys(obj).map(k => ({ key: k, value: obj[k] }))) {
      if (subObject.key.toLowerCase().includes(key.toLowerCase()) ||
        (subObject.value && subObject.value.toString && subObject.value.toString().toLowerCase().includes(key.toLowerCase()))) {
        return true;
      } else if (subObject.value && typeof subObject.value === 'object') {
        if (this.objectHasValueInTrees(subObject.value, key)) {
          return true;
        }
      }
    }
    return false;
  }

  public keyToLabel(key: string): string {
    return key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
  }

  public propertyKeyToDisplayLabel(key: string): string {
    const renamedKey = this.getRenamedPropertyKey(key);
    return this.showFriendly ? this.keyToLabel(renamedKey) : renamedKey;
  }

  public getNameForDisplay(): string {
    if (!this.showFriendly) {
      return this.name;
    }

    const formattedName = this.keyToLabel(this.name);
    const renamedKey = this.getRenamedPropertyKey(this.name);
    if (renamedKey !== this.name) {
      return renamedKey;
    }

    return formattedName;
  }

  public getRenamedPropertyKey(key: string): string {
    return this.propertyDisplayConfig?.renameProperties?.[key] || key;
  }

  public getChildPropertyDisplayConfig(key: string): PropertyDisplayConfig | undefined {
    const baseConfig = this.propertyDisplayConfig;
    if (!baseConfig) {
      return undefined;
    }

    const cachedConfig = this.resolvedChildPropertyDisplayConfigs[key];
    if (cachedConfig) {
      return cachedConfig;
    }

    const childOverride = baseConfig.childConfigs?.[key];
    if (!childOverride) {
      this.resolvedChildPropertyDisplayConfigs[key] = baseConfig;
      return baseConfig;
    }

    const resolvedConfig = {
      reorderProperties: childOverride.reorderProperties ?? baseConfig.reorderProperties,
      renameProperties: {
        ...(baseConfig.renameProperties || {}),
        ...(childOverride.renameProperties || {})
      },
      hideProperties: childOverride.hideProperties ?? baseConfig.hideProperties,
      childConfigs: childOverride.childConfigs || baseConfig.childConfigs
    };

    this.resolvedChildPropertyDisplayConfigs[key] = resolvedConfig;
    return resolvedConfig;
  }

  private isHiddenProperty(key: string): boolean {
    return this.propertyDisplayConfig?.hideProperties?.includes(key) || false;
  }

  private applyConfiguredPropertyOrder<T extends { key: string }>(properties: T[]): T[] {
    const configuredOrder = this.propertyDisplayConfig?.reorderProperties;
    if (!configuredOrder || configuredOrder.length === 0) {
      return properties;
    }

    const orderIndexMap = new Map<string, number>();
    configuredOrder.forEach((propertyName, index) => orderIndexMap.set(propertyName, index));

    return properties
      .map((property, originalIndex) => ({ property, originalIndex }))
      .sort((a, b) => {
        const aOrder = orderIndexMap.get(a.property.key);
        const bOrder = orderIndexMap.get(b.property.key);

        if (aOrder !== undefined && bOrder !== undefined) {
          return aOrder - bOrder;
        }

        if (aOrder !== undefined) {
          return -1;
        }

        if (bOrder !== undefined) {
          return 1;
        }

        return a.originalIndex - b.originalIndex;
      })
      .map(x => x.property);
  }

  public replaceEnumsForFriendlyDisplay(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (value === null || value === undefined) {
        continue;
      }
      const idKey = `${key}Id`;
      const descKey = `${key}Desc`;
      if (idKey in obj && descKey in obj) {
        obj[key] = obj[descKey];
        delete obj[idKey];
        delete obj[descKey];
      }
      // Recursively call on sub-objects that are not primitives
      else if (typeof value === 'object' && !Array.isArray(value)) {
        obj[key] = this.replaceEnumsForFriendlyDisplay(value);
      }
      else if (Array.isArray(value)) {
        obj[key] = value.map(item =>
          typeof item === 'object' && item !== null ? this.replaceEnumsForFriendlyDisplay(item) : item
        );
      }
    }

    return obj;
  }

  public replaceMeausurementUnitsForFriendlyDisplay(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (value === null || value === undefined) {
        continue;
      }

      const formattedValue = typeof value === 'number' ? parseFloat(value.toFixed(3)) : value;
      obj[key] = formattedValue;

      const unitKey = `${key}Unit`;
      if (unitKey in obj) {
        const unitValue = obj[unitKey];
        let unit = unitValue;
        if (unitValue && unitValue.includes('(') && unitValue.includes(')')) {
          const match = unitValue.match(/\(([^)]+)\)/);
          unit = match ? match[1] : unitValue;
        }
        obj[key] = `${formattedValue}${unit ? ' ' + unit : ''}`;
        delete obj[unitKey];
      }

      // Recursively call on sub-objects that are not primitives
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          obj[key] = value.map(item =>
            typeof item === 'object' && item !== null ? this.replaceMeausurementUnitsForFriendlyDisplay(item) : item
          );
        } else {
          obj[key] = this.replaceMeausurementUnitsForFriendlyDisplay(value);
        }
      }
    }

    return obj;
  }

  public removeJunkForFriendlyDisplay(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }
    for (const key of Object.keys(obj)) {
      if (key.startsWith('_') || key === 'id' || key === 'Id' || key === "ID") {
        delete obj[key];
      } else {
        const value = obj[key];
        if (typeof value === 'object' && value !== null) {
          if (Array.isArray(value)) {
            obj[key] = value.map(item =>
              typeof item === 'object' && item !== null ? this.removeJunkForFriendlyDisplay(item) : item
            );
          } else {
            obj[key] = this.removeJunkForFriendlyDisplay(value);
          }
        }
      }
    }
    return obj;
  }

  public formatDatesForFriendlyDisplay(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (value === null || value === undefined) {
        continue;
      }

      if (typeof value === 'string') {
        const datePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
        if (datePattern.test(value)) {
          const date = new Date(value);
          obj[key] = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
        }
      } else if (typeof value === 'object') {
        if (Array.isArray(value)) {
          obj[key] = value.map(item =>
            typeof item === 'object' && item !== null ? this.formatDatesForFriendlyDisplay(item) : item
          );
        } else {
          obj[key] = this.formatDatesForFriendlyDisplay(value);
        }
      }
    }

    return obj;
  }

  public removePricingForFriendlyDisplay(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    for (const key of Object.keys(obj)) {
      if (key.endsWith('Price') || key.endsWith('Cost') || key.endsWith('Total')) {
        delete obj[key];
      } else if (key !== 'DerivedPrices') {
        const value = obj[key];
        if (typeof value === 'object' && value !== null) {
          if (Array.isArray(value)) {
            obj[key] = value.map(item =>
              typeof item === 'object' && item !== null ? this.removePricingForFriendlyDisplay(item) : item
            );
          } else {
            obj[key] = this.removePricingForFriendlyDisplay(value);
          }
        }
      }
    }

    return obj;
  }

  public formatPricingForFriendlyDisplay(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (value === null || value === undefined) {
        continue;
      }

      if ((key.endsWith('Price') || key.endsWith('Cost') || key.endsWith('NetSell') ||
        key.endsWith('Total') || key.endsWith('Commission')) && typeof value === 'number') {
        obj[key] = `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      } else if (typeof value === 'object') {
        if (Array.isArray(value)) {
          obj[key] = value.map(item =>
            typeof item === 'object' && item !== null ? this.formatPricingForFriendlyDisplay(item) : item
          );
        } else {
          obj[key] = this.formatPricingForFriendlyDisplay(value);
        }
      }
    }

    return obj;
  }



}
